// components/auth/UnauthorizedPage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { 
  ShieldOff, 
  Home,
  ArrowLeft,
  Lock,
  Key,
  AlertCircle,
  Mail,
  LogOut
} from 'lucide-react';
import tunaLogo from '../../assets/tunasignalai_logo.svg';
import { SiteStyles } from '../site/SiteChrome';

const UnauthorizedPage = () => {
  useTheme();
  const darkMode = true; // public and account pages always use the site's dark terminal theme
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/stockwatch');
    }
  };

  const handleRequestAccess = () => {
    window.location.href = 'mailto:support@tunasignal.ai?subject=Access%20Request%20-%20Admin%20Privileges';
  };

  return (
    <div className="dark ts-page">
    <SiteStyles />
    <div className={`min-h-screen ${
      darkMode ? 'bg-[#07131F]' : 'bg-slate-50 dark:bg-[#13283C]'
    }`}>
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className={`w-full max-w-2xl ${
          darkMode ? 'bg-[#0D1E2E]' : 'bg-white'
        } rounded-md overflow-hidden`}>
          
          {/* Unauthorized Header */}
          <div className="relative bg-[#0D1E2E] border-b border-[#1C3348] p-8 text-white">
            
            <div className="relative">
              <ShieldOff className="w-7 h-7 mb-3 text-amber-400" aria-hidden="true" />
              <h1 className="text-2xl font-semibold mb-1">Access Denied</h1>
              <p className="text-slate-400">
                You don't have permission to access this area
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-8">
            {/* Access Information */}
            <div className={`rounded-lg p-6 mb-6 ${
              darkMode ? 'bg-[#13283C]' : 'bg-gray-50'
            }`}>
              <div className="flex items-center mb-4">
                <Lock className="w-5 h-5 text-slate-400 mr-2" />
                <h2 className={`text-lg font-semibold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Restricted Access
                </h2>
              </div>
              
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                The page you're trying to access requires special permissions that are not currently 
                associated with your account.
              </p>
              
              {currentUser && (
                <div className={`p-4 rounded-lg ${
                  darkMode ? 'bg-[#0D1E2E]' : 'bg-white'
                } border ${darkMode ? 'border-[#28445F]' : 'border-gray-200'}`}>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Current User:
                      </span>
                      <span className={`text-sm font-medium ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {currentUser.username || currentUser.email}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Account Type:
                      </span>
                      <span className={`text-sm font-medium ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {currentUser.roles?.includes('admin') ? 'Administrator' : 'Standard User'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Required Access:
                      </span>
                      <span className="text-sm font-medium text-slate-400">
                        Administrator
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* What You Can Do */}
            <div className={`rounded-lg border ${
              darkMode ? 'border-[#1C3348]' : 'border-gray-200'
            } p-6 mb-6`}>
              <h3 className={`font-semibold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                What You Can Do
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <Key className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Request Access
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      If you believe you should have access to this area, contact your administrator
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <ArrowLeft className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Go Back
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Return to the previous page or dashboard
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Home className="w-5 h-5 text-slate-400 mr-3 mt-0.5" />
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Home Page
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Navigate to the main landing page
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Notice */}
            <div className={`rounded-lg p-4 mb-6 border ${
              darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    <strong>Security Notice:</strong> This access attempt has been logged for security purposes. 
                    Repeated unauthorized access attempts may result in account suspension.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleGoBack}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                  darkMode 
                    ? 'bg-[#5AA9F0] hover:bg-[#8CC4F5] text-[#07131F]' 
                    : 'bg-[#5AA9F0] hover:bg-[#8CC4F5] text-[#07131F]'
                }`}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Go Back to Previous Page
              </button>
              
              <button
                onClick={() => navigate('/stockwatch')}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center border ${
                  darkMode 
                    ? 'border-[#28445F] hover:bg-[#13283C] text-gray-300' 
                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                }`}
              >
                <Home className="w-5 h-5 mr-2" />
                Go to Dashboard
              </button>
              
              <button
                onClick={handleRequestAccess}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                  darkMode 
                    ? 'text-slate-400 hover:bg-[#13283C] border border-[#28445F]' 
                    : 'text-slate-400 hover:bg-purple-50 border border-purple-200'
                }`}
              >
                <Mail className="w-5 h-5 mr-2" />
                Request Access
              </button>
              
              <button
                onClick={handleLogout}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                  darkMode 
                    ? 'text-red-400 hover:bg-red-900/20' 
                    : 'text-red-600 hover:bg-red-50'
                }`}
              >
                <LogOut className="w-5 h-5 mr-2" />
                Sign Out
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className={`px-8 py-4 border-t ${
            darkMode ? 'bg-[#07131F] border-[#1C3348]' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <img src={tunaLogo} alt="TunaSignal AI" className="h-6" />
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {new Date().toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default UnauthorizedPage;